import { Input as InputPrimitive } from '@base-ui/react/input';
import * as React from 'react';

import { cn } from '@/lib/utils';

export type InputSize = 'xs' | 'sm' | 'md' | 'lg';
export type InputShape = 'rounded' | 'round';
export type InputState = 'default' | 'success' | 'destructive';

export interface InputProps
  extends Omit<React.ComponentProps<'input'>, 'size'> {
  /** Input size variant */
  size?: InputSize;
  /** Input shape variant */
  shape?: InputShape;
  /** Input state for validation feedback */
  state?: InputState;
  /** Whether to show an outline/border variant */
  hasOutline?: boolean;
}

export const sizeClasses: Record<InputSize, string> = {
  xs: 'h-[26px] px-3 py-0.5 text-sm gap-0.5',
  sm: 'h-8 px-3 py-1 text-sm gap-1.5',
  md: 'h-9 px-3 py-1 text-sm gap-1.5',
  lg: 'h-10 px-3 py-1 text-sm gap-1.5',
};

export const shapeClasses: Record<InputShape, string> = {
  rounded: 'rounded-xl',
  round: 'rounded-full',
};

export const stateClasses: Record<InputState, string> = {
  default: '',
  success: 'border-success',
  destructive: 'border-destructive',
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      size = 'md',
      shape = 'rounded',
      state = 'default',
      hasOutline,
      ...props
    },
    ref,
  ) => {
    const isDestructive = state === 'destructive';

    return (
      <InputPrimitive
        ref={ref}
        type={type}
        data-slot="input"
        data-state={state}
        aria-invalid={isDestructive}
        className={cn(
          'bg-custom-background-input-30 border-input focus-visible:border-ring focus-visible:ring-gray-400/50 border bg-transparent motion-safe:transition-[color,border-color,box-shadow] duration-(--duration-fast) ease-standard motion-reduce:transition-none file:h-7 file:text-sm file:font-medium focus-visible:ring-[3px] file:text-foreground placeholder:text-muted-foreground w-full min-w-0 outline-none file:inline-flex file:border-0 file:bg-transparent disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-muted read-only:bg-muted read-only:border-border read-only:cursor-default',
          sizeClasses[size],
          shapeClasses[shape],
          stateClasses[state],
          hasOutline &&
            'border-border ring-1 ring-border focus-visible:ring-2 focus-visible:ring-ring',
          className,
        )}
        {...props}
      />
    );
  },
);

Input.displayName = 'Input';

export { Input };
